export const ramadanSetupStatuses = ["draft", "ready", "active", "completed", "archived"] as const;
export type RamadanSetupStatus = (typeof ramadanSetupStatuses)[number];

export const ramadanPrayerIds = ["fajr", "dhuhr", "asr", "maghrib", "isha", "jumuah"] as const;
export type RamadanPrayerId = (typeof ramadanPrayerIds)[number];

export type RamadanLocalizedText = {
  ar: string | null;
  en: string;
};

export type RamadanPrayerOverride = {
  displayTime: string | null;
  enabled: boolean;
  iqamaTime: string | null;
  note: RamadanLocalizedText | null;
  prayerId: RamadanPrayerId;
};

export const ramadanIqamaPhaseIds = ["firstTen", "secondTen", "lastTen"] as const;
export type RamadanIqamaPhaseId = (typeof ramadanIqamaPhaseIds)[number];

export type RamadanIqamaPhaseConfiguration = {
  endsOn: string;
  label: RamadanLocalizedText;
  phaseId: RamadanIqamaPhaseId;
  prayerOverrides: RamadanPrayerOverride[];
  startsOn: string;
};

export type RamadanTahajjudNightConfiguration = {
  enabled: boolean;
  gregorianDate: string;
  nightId: string;
  notes: RamadanLocalizedText | null;
  startTime: string;
};

export type RamadanGuestImamProfile = {
  bio: RamadanLocalizedText | null;
  displayName: RamadanLocalizedText;
  enabled: boolean;
  imamId: string;
  photoUrl: string | null;
  sortOrder: number;
  title: RamadanLocalizedText | null;
};

export type RamadanGuestImamConfiguration = {
  enabled: boolean;
  profiles: RamadanGuestImamProfile[];
};

export type RamadanCampusPrayerConfiguration = {
  campusId: string;
  prayerOverrides: RamadanPrayerOverride[];
  sunrise: {
    display: boolean;
    label: RamadanLocalizedText | null;
  };
  timezone: string | null;
};

export type RamadanTaraweehCampusConfiguration = {
  campusId: string;
  enabled: boolean;
  notes: RamadanLocalizedText | null;
  rakahCount: number | null;
  relativeToIshaMinutes: number | null;
  startTime: string | null;
};

export type RamadanQiyamCampusConfiguration = {
  campusId: string;
  enabled: boolean;
  endsOn: string | null;
  notes: RamadanLocalizedText | null;
  startsOn: string | null;
  startTime: string | null;
};

export type RamadanLastTenNightsConfiguration = {
  approvedStartsOn: string | null;
  enabled: boolean;
  homeMessage: RamadanLocalizedText | null;
  homeTitle: RamadanLocalizedText | null;
  qiyamPriority: boolean;
};

export type RamadanHomeConfiguration = {
  message: RamadanLocalizedText;
  title: RamadanLocalizedText;
};

export type RamadanDisplayPhase = "before" | "active" | "after";
export type RamadanExperiencePhase = "closed" | "preRamadanOpen" | "ramadanActive" | "ramadanEnded";

export type RamadanComputedState = {
  evaluatedAt: unknown;
  lastTenNightsActive: boolean;
  lastTenNightsStartsOn: string | null;
  phase: RamadanDisplayPhase;
  ramadanDay: number | null;
};

export type RamadanSetupRecord = {
  archivedAt: unknown | null;
  campusPrayerConfigs: RamadanCampusPrayerConfiguration[];
  completedAt: unknown | null;
  computed: RamadanComputedState;
  createdAt: unknown;
  createdBy: string;
  defaultCampusId: string;
  endAdjustmentDays: number;
  endsOn: string;
  experienceOpensAt: string | null;
  featuredRamadanContentId: string | null;
  hijriYear: number;
  home: RamadanHomeConfiguration;
  guestImams?: RamadanGuestImamConfiguration;
  iqamaPhases?: RamadanIqamaPhaseConfiguration[];
  lastTenNights: RamadanLastTenNightsConfiguration;
  linkedOperationalModeId: string;
  organizationId: string;
  qiyam: RamadanQiyamCampusConfiguration[];
  ramadanId: string;
  source: "experience_thirty_two";
  startAdjustmentDays: number;
  startsOn: string;
  status: RamadanSetupStatus;
  sponsorshipOpensAt: string | null;
  tahajjudNights?: RamadanTahajjudNightConfiguration[];
  taraweeh: RamadanTaraweehCampusConfiguration[];
  timezone: string;
  updatedAt: unknown;
  updatedBy: string;
};

export type RamadanPrayerDisplaySource =
  "ramadanOverride" | "normalCampus" | "provider" | "unavailable";

export type RamadanPrayerDisplay = {
  note: RamadanLocalizedText | null;
  source: RamadanPrayerDisplaySource;
  time: string | null;
};

const safeIdentifierPattern = /^[A-Za-z0-9][A-Za-z0-9_-]{1,119}$/u;
const localDatePattern = /^\d{4}-\d{2}-\d{2}$/u;
const localTimePattern = /^([01]\d|2[0-3]):[0-5]\d$/u;

const allowedTransitions: Record<RamadanSetupStatus, readonly RamadanSetupStatus[]> = {
  active: ["active", "completed", "archived"],
  archived: ["archived"],
  completed: ["completed", "archived"],
  draft: ["draft", "ready", "archived"],
  ready: ["ready", "active", "draft", "completed", "archived"],
};

export function buildRamadanSetupId(hijriYear: number): string {
  return `ramadan-${hijriYear}`;
}

export function isRamadanSetupStatus(value: unknown): value is RamadanSetupStatus {
  return typeof value === "string" && ramadanSetupStatuses.includes(value as RamadanSetupStatus);
}

export function isRamadanPrayerId(value: unknown): value is RamadanPrayerId {
  return typeof value === "string" && ramadanPrayerIds.includes(value as RamadanPrayerId);
}

export function isRamadanSetupTransitionAllowed(
  from: RamadanSetupStatus,
  to: RamadanSetupStatus,
): boolean {
  return allowedTransitions[from].includes(to);
}

export function validateRamadanSetupRecord(
  setup: RamadanSetupRecord,
  expectedOrganizationId?: string,
): string[] {
  const errors: string[] = [];

  if (!isSafeIdentifier(setup.ramadanId)) {
    errors.push("Ramadan setup ID must be a safe stable identifier.");
  }
  if (!isSafeIdentifier(setup.organizationId)) {
    errors.push("Ramadan setup organization ID must be a safe stable identifier.");
  }
  if (expectedOrganizationId !== undefined && setup.organizationId !== expectedOrganizationId) {
    errors.push("Ramadan setup organization ID must match its path.");
  }
  if (!Number.isInteger(setup.hijriYear) || setup.hijriYear < 1300 || setup.hijriYear > 1600) {
    errors.push("Ramadan Hijri year must be a supported integer.");
  }
  if (setup.ramadanId !== buildRamadanSetupId(setup.hijriYear)) {
    errors.push("Ramadan setup ID must match its Hijri year.");
  }
  if (!isRamadanSetupStatus(setup.status)) {
    errors.push("Ramadan setup status must be canonical.");
  }
  if (!isLocalDateString(setup.startsOn) || !isLocalDateString(setup.endsOn)) {
    errors.push("Ramadan approved start and end dates must be YYYY-MM-DD.");
  } else {
    const duration = daysBetweenLocalDates(setup.startsOn, setup.endsOn) + 1;
    if (duration < 1 || duration > 31) {
      errors.push("Ramadan approved date range must be between 1 and 31 days.");
    }
  }
  if (!isSupportedTimeZone(setup.timezone)) {
    errors.push("Ramadan timezone must be a supported IANA timezone.");
  }
  if (!isOptionalDateTimeOrLocalDate(setup.experienceOpensAt)) {
    errors.push("Ramadan experience open date must be a valid local date or date-time.");
  }
  if (!isOptionalDateTimeOrLocalDate(setup.sponsorshipOpensAt)) {
    errors.push("Ramadan sponsorship open date must be a valid local date or date-time.");
  }
  if (
    setup.featuredRamadanContentId !== null &&
    !isSafeContentIdentifier(setup.featuredRamadanContentId)
  ) {
    errors.push("Featured Ramadan content ID must be a safe content identifier.");
  }
  if (!Number.isInteger(setup.startAdjustmentDays) || Math.abs(setup.startAdjustmentDays) > 3) {
    errors.push("Ramadan start adjustment must be an integer day offset from -3 to 3.");
  }
  if (!Number.isInteger(setup.endAdjustmentDays) || Math.abs(setup.endAdjustmentDays) > 3) {
    errors.push("Ramadan end adjustment must be an integer day offset from -3 to 3.");
  }
  if (!isSafeIdentifier(setup.linkedOperationalModeId)) {
    errors.push("Linked Ramadan operational mode ID must be a safe stable identifier.");
  }
  if (!isSafeIdentifier(setup.defaultCampusId)) {
    errors.push("Default Ramadan campus ID must be a safe stable identifier.");
  }
  if (!isLocalizedText(setup.home.title, 140) || !isLocalizedText(setup.home.message, 500)) {
    errors.push("Ramadan Home configuration must include localized title and message.");
  }
  if (!isValidCampusPrayerConfigs(setup.campusPrayerConfigs, setup.defaultCampusId)) {
    errors.push("Ramadan campus prayer configuration must be campus-scoped and canonical.");
  }
  if (!isValidTaraweehConfig(setup.taraweeh)) {
    errors.push("Ramadan Taraweeh configuration must be structured and campus-scoped.");
  }
  if (!isValidQiyamConfig(setup.qiyam, setup.startsOn, setup.endsOn)) {
    errors.push("Ramadan Qiyam configuration must use valid dates and campus-scoped times.");
  }
  if (!isValidLastTenNightsConfig(setup.lastTenNights, setup.startsOn, setup.endsOn)) {
    errors.push("Ramadan last-ten-nights configuration must follow approved Ramadan dates.");
  }
  if (!isValidIqamaPhases(setup.iqamaPhases ?? [], setup.startsOn, setup.endsOn)) {
    errors.push("Ramadan phased iqama configuration must define up to three valid date windows.");
  }
  if (!isValidTahajjudNights(setup.tahajjudNights ?? [], setup.startsOn, setup.endsOn)) {
    errors.push("Ramadan Tahajjud nights must be selected dates within the approved range.");
  }
  if (!isValidGuestImams(setup.guestImams)) {
    errors.push("Ramadan guest imam profiles must be valid and organization-controlled.");
  }

  return errors;
}

export function buildRamadanComputedState(
  setup: Pick<RamadanSetupRecord, "endsOn" | "lastTenNights" | "startsOn" | "timezone">,
  evaluatedAt: unknown,
): RamadanComputedState {
  const evaluatedDate = instantToDate(evaluatedAt);
  const localDate = evaluatedDate ? localDateStringForInstant(evaluatedDate, setup.timezone) : null;
  const lastTenNightsStartsOn =
    setup.lastTenNights.approvedStartsOn ?? addDaysToLocalDate(setup.endsOn, -9);

  if (localDate === null || compareLocalDates(localDate, setup.startsOn) < 0) {
    return {
      evaluatedAt,
      lastTenNightsActive: false,
      lastTenNightsStartsOn,
      phase: "before",
      ramadanDay: null,
    };
  }

  if (compareLocalDates(localDate, setup.endsOn) > 0) {
    return {
      evaluatedAt,
      lastTenNightsActive: false,
      lastTenNightsStartsOn,
      phase: "after",
      ramadanDay: null,
    };
  }

  const ramadanDay = daysBetweenLocalDates(setup.startsOn, localDate) + 1;
  return {
    evaluatedAt,
    lastTenNightsActive:
      setup.lastTenNights.enabled && compareLocalDates(localDate, lastTenNightsStartsOn) >= 0,
    lastTenNightsStartsOn,
    phase: "active",
    ramadanDay,
  };
}

export function resolveRamadanExperiencePhase(
  setup: Pick<
    RamadanSetupRecord,
    "endsOn" | "experienceOpensAt" | "startsOn" | "status" | "timezone"
  >,
  evaluatedAt: unknown,
): RamadanExperiencePhase {
  if (setup.status !== "active") {
    return "closed";
  }

  const evaluatedDate = instantToDate(evaluatedAt);
  const localDate = evaluatedDate ? localDateStringForInstant(evaluatedDate, setup.timezone) : null;
  if (localDate === null) {
    return "closed";
  }

  const experienceOpensOn = localDateFromDateTimeOrLocalDate(setup.experienceOpensAt);
  if (experienceOpensOn !== null && compareLocalDates(localDate, experienceOpensOn) < 0) {
    return "closed";
  }
  if (experienceOpensOn === null && compareLocalDates(localDate, setup.startsOn) < 0) {
    return "closed";
  }
  if (compareLocalDates(localDate, setup.startsOn) < 0) {
    return "preRamadanOpen";
  }
  if (compareLocalDates(localDate, setup.endsOn) > 0) {
    return "ramadanEnded";
  }
  return "ramadanActive";
}

export function isRamadanSponsorshipOpen(
  setup: Pick<RamadanSetupRecord, "sponsorshipOpensAt" | "timezone">,
  evaluatedAt: unknown,
): boolean {
  const sponsorshipOpensOn = localDateFromDateTimeOrLocalDate(setup.sponsorshipOpensAt);
  if (sponsorshipOpensOn === null) {
    return true;
  }
  const evaluatedDate = instantToDate(evaluatedAt);
  const localDate = evaluatedDate ? localDateStringForInstant(evaluatedDate, setup.timezone) : null;
  return localDate !== null && compareLocalDates(localDate, sponsorshipOpensOn) >= 0;
}

export function resolveRamadanPrayerDisplay(input: {
  campusId: string;
  normalCampusTime?: string | null;
  prayerId: RamadanPrayerId;
  providerTime?: string | null;
  setup: RamadanSetupRecord;
}): RamadanPrayerDisplay {
  const campusConfig =
    input.setup.campusPrayerConfigs.find((config) => config.campusId === input.campusId) ??
    input.setup.campusPrayerConfigs.find(
      (config) => config.campusId === input.setup.defaultCampusId,
    );
  const override = campusConfig?.prayerOverrides.find(
    (candidate) => candidate.prayerId === input.prayerId,
  );

  if (override?.enabled === true && override.iqamaTime !== null) {
    return {
      note: override.note,
      source: "ramadanOverride",
      time: override.iqamaTime,
    };
  }
  if (isLocalTime(input.normalCampusTime)) {
    return {
      note: null,
      source: "normalCampus",
      time: input.normalCampusTime,
    };
  }
  if (isLocalTime(input.providerTime)) {
    return {
      note: null,
      source: "provider",
      time: input.providerTime,
    };
  }

  return {
    note: null,
    source: "unavailable",
    time: null,
  };
}

export function ramadanIqamaPhaseForDay(
  setup: Pick<RamadanSetupRecord, "computed" | "iqamaPhases">,
): RamadanIqamaPhaseConfiguration | null {
  const ramadanDay = setup.computed.ramadanDay;
  if (typeof ramadanDay !== "number" || !Number.isInteger(ramadanDay)) {
    return null;
  }

  const phases = setup.iqamaPhases ?? [];
  if (phases.length === 0) {
    return null;
  }

  if (ramadanDay <= 10) {
    return phases.find((phase) => phase.phaseId === "firstTen") ?? null;
  }
  if (ramadanDay <= 20) {
    return phases.find((phase) => phase.phaseId === "secondTen") ?? null;
  }
  return phases.find((phase) => phase.phaseId === "lastTen") ?? null;
}

export function localDateStringForInstant(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone,
    year: "numeric",
  }).formatToParts(instant);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    throw new Error("Unable to format local date for timezone.");
  }

  return `${year}-${month}-${day}`;
}

export function addDaysToLocalDate(value: string, days: number): string {
  const date = localDateToUtcDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function daysBetweenLocalDates(start: string, end: string): number {
  return Math.floor(
    (localDateToUtcDate(end).getTime() - localDateToUtcDate(start).getTime()) /
      (24 * 60 * 60 * 1000),
  );
}

function isValidCampusPrayerConfigs(
  configs: RamadanCampusPrayerConfiguration[],
  defaultCampusId: string,
): boolean {
  if (!Array.isArray(configs) || configs.length === 0 || configs.length > 20) {
    return false;
  }

  const campusIds = new Set<string>();
  let hasDefaultCampus = false;
  for (const config of configs) {
    if (!isSafeIdentifier(config.campusId) || campusIds.has(config.campusId)) {
      return false;
    }
    campusIds.add(config.campusId);
    hasDefaultCampus ||= config.campusId === defaultCampusId;
    if (config.timezone !== null && !isSupportedTimeZone(config.timezone)) {
      return false;
    }
    if (!isValidSunriseConfig(config.sunrise)) {
      return false;
    }
    const prayerIds = new Set<string>();
    for (const override of config.prayerOverrides) {
      if (
        !isRamadanPrayerId(override.prayerId) ||
        prayerIds.has(override.prayerId) ||
        !isOptionalLocalTime(override.iqamaTime) ||
        !isOptionalLocalTime(override.displayTime) ||
        !isOptionalLocalizedText(override.note, 180)
      ) {
        return false;
      }
      prayerIds.add(override.prayerId);
    }
  }

  return hasDefaultCampus;
}

function isValidSunriseConfig(value: RamadanCampusPrayerConfiguration["sunrise"]): boolean {
  return typeof value.display === "boolean" && isOptionalLocalizedText(value.label, 120);
}

function isValidTaraweehConfig(configs: RamadanTaraweehCampusConfiguration[]): boolean {
  if (!Array.isArray(configs) || configs.length > 20) {
    return false;
  }

  const campusIds = new Set<string>();
  for (const config of configs) {
    if (!isSafeIdentifier(config.campusId) || campusIds.has(config.campusId)) {
      return false;
    }
    campusIds.add(config.campusId);
    if (
      typeof config.enabled !== "boolean" ||
      !isOptionalLocalTime(config.startTime) ||
      !isOptionalInteger(config.relativeToIshaMinutes, 0, 180) ||
      !isOptionalInteger(config.rakahCount, 0, 40) ||
      !isOptionalLocalizedText(config.notes, 240)
    ) {
      return false;
    }
  }

  return true;
}

function isValidQiyamConfig(
  configs: RamadanQiyamCampusConfiguration[],
  startsOn: string,
  endsOn: string,
): boolean {
  if (!Array.isArray(configs) || configs.length > 20) {
    return false;
  }

  const campusIds = new Set<string>();
  for (const config of configs) {
    if (!isSafeIdentifier(config.campusId) || campusIds.has(config.campusId)) {
      return false;
    }
    campusIds.add(config.campusId);
    if (
      typeof config.enabled !== "boolean" ||
      !isOptionalLocalTime(config.startTime) ||
      !isOptionalLocalDateWithinRange(config.startsOn, startsOn, endsOn) ||
      !isOptionalLocalDateWithinRange(config.endsOn, startsOn, endsOn) ||
      !isOptionalLocalizedText(config.notes, 240)
    ) {
      return false;
    }
    if (
      config.startsOn !== null &&
      config.endsOn !== null &&
      compareLocalDates(config.endsOn, config.startsOn) < 0
    ) {
      return false;
    }
  }

  return true;
}

function isValidLastTenNightsConfig(
  config: RamadanLastTenNightsConfiguration,
  startsOn: string,
  endsOn: string,
): boolean {
  return (
    typeof config.enabled === "boolean" &&
    typeof config.qiyamPriority === "boolean" &&
    isOptionalLocalDateWithinRange(config.approvedStartsOn, startsOn, endsOn) &&
    isOptionalLocalizedText(config.homeTitle, 140) &&
    isOptionalLocalizedText(config.homeMessage, 500)
  );
}

function isValidIqamaPhases(
  phases: RamadanIqamaPhaseConfiguration[],
  startsOn: string,
  endsOn: string,
): boolean {
  if (!Array.isArray(phases) || phases.length > 3) {
    return false;
  }

  const phaseIds = new Set<string>();
  for (const phase of phases) {
    if (
      !ramadanIqamaPhaseIds.includes(phase.phaseId) ||
      phaseIds.has(phase.phaseId) ||
      !isLocalizedText(phase.label, 80) ||
      !isLocalDateString(phase.startsOn) ||
      !isLocalDateString(phase.endsOn) ||
      compareLocalDates(phase.startsOn, startsOn) < 0 ||
      compareLocalDates(phase.endsOn, endsOn) > 0 ||
      compareLocalDates(phase.endsOn, phase.startsOn) < 0 ||
      !Array.isArray(phase.prayerOverrides) ||
      phase.prayerOverrides.length > ramadanPrayerIds.length
    ) {
      return false;
    }

    const prayerIds = new Set<string>();
    for (const override of phase.prayerOverrides) {
      if (
        !isRamadanPrayerId(override.prayerId) ||
        prayerIds.has(override.prayerId) ||
        !isOptionalLocalTime(override.iqamaTime) ||
        !isOptionalLocalTime(override.displayTime) ||
        !isOptionalLocalizedText(override.note, 180)
      ) {
        return false;
      }
      prayerIds.add(override.prayerId);
    }
    phaseIds.add(phase.phaseId);
  }

  return true;
}

function isValidTahajjudNights(
  nights: RamadanTahajjudNightConfiguration[],
  startsOn: string,
  endsOn: string,
): boolean {
  if (!Array.isArray(nights) || nights.length > 10) {
    return false;
  }

  const nightIds = new Set<string>();
  for (const night of nights) {
    if (
      !isSafeIdentifier(night.nightId) ||
      nightIds.has(night.nightId) ||
      typeof night.enabled !== "boolean" ||
      !isLocalTime(night.startTime) ||
      !isOptionalLocalizedText(night.notes, 240) ||
      !isLocalDateString(night.gregorianDate) ||
      compareLocalDates(night.gregorianDate, startsOn) < 0 ||
      compareLocalDates(night.gregorianDate, endsOn) > 0
    ) {
      return false;
    }
    nightIds.add(night.nightId);
  }

  return true;
}

function isValidGuestImams(config: RamadanGuestImamConfiguration | undefined): boolean {
  if (config === undefined) {
    return true;
  }

  if (
    typeof config.enabled !== "boolean" ||
    !Array.isArray(config.profiles) ||
    config.profiles.length > 12
  ) {
    return false;
  }

  const imamIds = new Set<string>();
  for (const profile of config.profiles) {
    if (
      !isSafeIdentifier(profile.imamId) ||
      imamIds.has(profile.imamId) ||
      typeof profile.enabled !== "boolean" ||
      !isLocalizedText(profile.displayName, 120) ||
      !isOptionalLocalizedText(profile.title, 140) ||
      !isOptionalLocalizedText(profile.bio, 500) ||
      !isOptionalInteger(profile.sortOrder, 0, 1000) ||
      (profile.photoUrl !== null &&
        (typeof profile.photoUrl !== "string" ||
          profile.photoUrl.trim().length === 0 ||
          profile.photoUrl.length > 500))
    ) {
      return false;
    }
    imamIds.add(profile.imamId);
  }

  return true;
}

function isOptionalLocalDateWithinRange(
  value: string | null,
  startsOn: string,
  endsOn: string,
): boolean {
  return (
    value === null ||
    (isLocalDateString(value) &&
      compareLocalDates(value, startsOn) >= 0 &&
      compareLocalDates(value, endsOn) <= 0)
  );
}

function isLocalizedText(value: RamadanLocalizedText, maxSize: number): boolean {
  return (
    isNonEmptyText(value.en, maxSize) && (value.ar === null || isNonEmptyText(value.ar, maxSize))
  );
}

function isOptionalLocalizedText(value: RamadanLocalizedText | null, maxSize: number): boolean {
  return value === null || isLocalizedText(value, maxSize);
}

function isNonEmptyText(value: unknown, maxSize: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= maxSize;
}

function isSafeIdentifier(value: unknown): value is string {
  return typeof value === "string" && safeIdentifierPattern.test(value);
}

function isLocalDateString(value: unknown): value is string {
  if (typeof value !== "string" || !localDatePattern.test(value)) {
    return false;
  }

  return localDateToUtcDate(value).toISOString().slice(0, 10) === value;
}

function isOptionalDateTimeOrLocalDate(value: unknown): value is string | null {
  return value === null || localDateFromDateTimeOrLocalDate(value) !== null;
}

function localDateFromDateTimeOrLocalDate(value: unknown): string | null {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }
  const trimmed = value.trim();
  const localDate = trimmed.slice(0, 10);
  if (!isLocalDateString(localDate)) {
    return null;
  }
  if (trimmed.length === 10) {
    return localDate;
  }
  const parsed = new Date(trimmed);
  return Number.isFinite(parsed.getTime()) ? localDate : null;
}

function isSafeContentIdentifier(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9][a-z0-9_-]{0,79}$/u.test(value);
}

function isOptionalLocalTime(value: unknown): value is string | null {
  return value === null || isLocalTime(value);
}

function isLocalTime(value: unknown): value is string {
  return typeof value === "string" && localTimePattern.test(value);
}

function isOptionalInteger(value: unknown, min: number, max: number): value is number | null {
  return (
    value === null ||
    (typeof value === "number" && Number.isInteger(value) && value >= min && value <= max)
  );
}

function isSupportedTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return value.trim().length > 0;
  } catch {
    return false;
  }
}

export function compareLocalDates(left: string, right: string): number {
  return localDateToUtcDate(left).getTime() - localDateToUtcDate(right).getTime();
}

function localDateToUtcDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day!));
}

function instantToDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }
  if (typeof value === "object" && value !== null && "toDate" in value) {
    const toDate = value.toDate;
    if (typeof toDate === "function") {
      const date = toDate.call(value) as Date;
      return Number.isFinite(date.getTime()) ? date : null;
    }
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "seconds" in value &&
    typeof value.seconds === "number"
  ) {
    const nanos =
      "nanoseconds" in value && typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
    return new Date(value.seconds * 1000 + Math.floor(nanos / 1_000_000));
  }

  return null;
}
